/**
 * questions-store (jarvis-today PH-10): Build Desk questions shown as cards in Approvals.
 *
 * The queue comes from GET /api/engine/build-desk/queue (read only, ranked, at most five). Answers go through the
 * existing answer route (api.answerBuildDesk) via the Today card; a question leaves only after its answer saved and
 * the queue was read again. `questionCount` is every open question, for the Approvals counts.
 *
 * PH-10b: the queue also carries decision needs (type "decision", keyed by their UUID), answered through
 * api.answerDecision with the version they were drawn from. A Desk question is keyed by its number.
 */

import { signal } from '@preact/signals';
import { api } from '../../core/api/client';
import type { QueueItem } from '../../core/api/types';
import { setCurrentApproval } from './approvals-store';

export const questions = signal<QueueItem[]>([]);
export const questionCount = signal(0);
export const questionsUnreadable = signal(false);
export const questionsLoaded = signal(false);
/** The question open in the card pane (or over the list on the phone): a Desk number or a decision's UUID. */
export const currentQuestionId = signal<number | string | null>(null);

/** The key an item is opened by: a Desk question's number, a decision's UUID. */
export function questionKey(item: QueueItem): number | string {
  return item.type === 'decision' ? item.need_id : item.id;
}

export const QUESTIONS_UNREADABLE = 'Your questions could not be read right now.';
export const QUESTION_GONE = 'That question was already answered or closed.';

let inFlight: Promise<void> | null = null;

export function loadQuestions(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const res = await api.buildDeskQueue();
      if (!res || !Array.isArray(res.items)) throw new Error('unreadable');
      questions.value = res.items;
      questionCount.value = Number.isInteger(res.total_open) ? res.total_open : res.items.length;
      questionsUnreadable.value = false;
    } catch {
      questionsUnreadable.value = true;   // keep what was on screen; never claim zero
    } finally {
      questionsLoaded.value = true;
      inFlight = null;
    }
  })();
  return inFlight;
}

export function openQuestion(id: number | string): void {
  setCurrentApproval(null);
  currentQuestionId.value = id;
}

export function closeQuestion(): void {
  currentQuestionId.value = null;
}

/** "desk-279" names question 279; "decision-<uuid>" names that decision; anything else names no question. */
export function questionFromRouteId(id: string | null | undefined): number | string | null {
  const match = /^desk-([1-9][0-9]{0,11})$/.exec(id ?? '');
  if (match) return Number(match[1]);
  const decision = /^decision-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/.exec(id ?? '');
  return decision ? decision[1] : null;
}

/** "/approvals?q=desk-279" opens question 279 (the notice link itself is "/approvals/desk-279", see index.tsx). */
export function questionFromLocation(search: string): number | string | null {
  return questionFromRouteId(new URLSearchParams(search).get('q'));
}

/** A fresh read: one already in flight may have started before the change, so wait for it, then read again. */
export async function reloadQuestions(): Promise<void> {
  if (inFlight) await inFlight;
  await loadQuestions();
}

export async function questionAnswered(): Promise<void> {
  closeQuestion();
  // A read that started before the save may still list the answered question: wait for it, then read again.
  if (inFlight) await inFlight;
  await loadQuestions();
}
